import { Link } from 'react-router';
import type { OverviewDTO } from '@devrobot/shared';
import { ArrowRight, Boxes, CircleCheck, FolderGit2, ListChecks, Loader, Server, SlidersHorizontal, TriangleAlert } from 'lucide-react';
import { useOverview } from '../api/queries';
import { useUser } from '../api/user';
import { ErrorAlert } from '../components/ErrorAlert';
import { JobsTable } from '../components/JobsTable';
import { PageHeader, Section } from '../components/PageHeader';
import { PageSkeleton } from '../components/Skeleton';
import { useCountUp } from '../kit/hooks';

const ATTENTION_ICON: Record<OverviewDTO['attention'][number]['kind'], typeof Server> = {
  vps: Server,
  target: Boxes,
  job: ListChecks,
  config: SlidersHorizontal,
};

type Tone = 'primary' | 'secondary' | 'accent' | 'info' | 'success' | 'warning' | 'error';
type Ink = 'warning' | 'error' | undefined;

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 19 ? 'Good afternoon' : 'Good evening';
}

/** A number that eases into place; the right value is rendered from the start. */
function Count({ value }: { value: number }) {
  const ref = useCountUp<HTMLSpanElement>(value);
  return (
    <span ref={ref} className="tabular-nums">
      {value.toLocaleString()}
    </span>
  );
}

function Stat({ title, value, desc, to, icon: Icon, tone, ink }: { title: string; value: React.ReactNode; desc?: React.ReactNode; to: string; icon: typeof Server; tone: Tone; ink?: Ink }) {
  return (
    <Link to={to} className="dr-surface dr-lift group flex min-w-0 flex-col gap-2 rounded-box p-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary sm:p-5">
      <div className="flex items-center justify-between gap-2">
        <h2 className="truncate text-sm font-medium text-base-content/65">{title}</h2>
        <span className={`dr-tint-${tone} grid size-8 shrink-0 place-items-center rounded-full`}>
          <Icon className="size-4" aria-hidden="true" />
        </span>
      </div>
      <p className="text-2xl font-semibold tracking-tight whitespace-nowrap sm:text-3xl">{value}</p>
      {desc ? <p className={`text-xs ${ink ? `dr-ink-${ink} font-medium` : 'text-base-content/55'}`}>{desc}</p> : null}
    </Link>
  );
}

export default function OverviewPage() {
  const user = useUser();
  const q = useOverview();

  if (q.isPending) return <PageSkeleton />;
  if (q.isError) return <ErrorAlert error={q.error} onRetry={() => void q.refetch()} />;
  const { counts, attention, recentJobs } = q.data;
  const disconnected = counts.vps - counts.vpsConnected;
  const firstName = (user.name || user.username).split(' ')[0];

  return (
    <>
      <PageHeader
        title={`${greeting()}, ${firstName}`}
        subtitle={
          <>
            <span className="sr-only">Overview. </span>
            {`Signed in as ${user.name} · GitLab ${user.groupRole}${user.platformAdmin ? ' · platform administrator' : ''}`}
          </>
        }
      />
      <div className="dr-stagger grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5">
        <Stat title="Projects" value={<Count value={counts.projects} />} desc="you can access" to="/projects" icon={FolderGit2} tone="primary" />
        <Stat
          title="VPS connected"
          value={
            <>
              <Count value={counts.vpsConnected} />
              <span className="text-base-content/40">/</span>
              <Count value={counts.vps} />
            </>
          }
          desc={disconnected > 0 ? `${disconnected} not connected` : 'all connected'}
          ink={disconnected > 0 ? 'warning' : undefined}
          to="/vps"
          icon={Server}
          tone={disconnected > 0 ? 'warning' : 'info'}
        />
        <Stat
          title="Deployment targets"
          value={<Count value={counts.targets} />}
          desc={counts.unhealthyTargets > 0 ? `${counts.unhealthyTargets} unhealthy` : 'none unhealthy'}
          ink={counts.unhealthyTargets > 0 ? 'error' : undefined}
          to="/projects"
          icon={Boxes}
          tone={counts.unhealthyTargets > 0 ? 'error' : 'secondary'}
        />
        <Stat title="Active jobs" value={<Count value={counts.runningJobs} />} desc="queued, running or awaiting" to="/jobs?active=1" icon={Loader} tone="info" />
        <Stat
          title="Failed jobs"
          value={<Count value={counts.failedJobs24h} />}
          desc="in the last 24 hours"
          ink={counts.failedJobs24h > 0 ? 'error' : undefined}
          to="/jobs?state=failed"
          icon={counts.failedJobs24h > 0 ? TriangleAlert : CircleCheck}
          tone={counts.failedJobs24h > 0 ? 'error' : 'success'}
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-3">
        <Section title="Needs attention" description="Problems that need a person: disconnected agents, unhealthy targets, pending configuration and DNS or certificate issues." className="xl:col-span-1">
          {attention.length === 0 ? (
            <div className="flex animate-fade items-center gap-3 rounded-field bg-base-200/60 p-3 text-sm text-base-content/70">
              <span className="dr-tint-success grid size-9 shrink-0 place-items-center rounded-full">
                <CircleCheck className="size-5" aria-hidden="true" />
              </span>
              Nothing needs attention right now.
            </div>
          ) : (
            <ul className="list -mx-2">
              {attention.map((a, i) => {
                const Icon = ATTENTION_ICON[a.kind];
                return (
                  <li key={`${a.kind}-${a.id}-${i}`} className="list-row items-start rounded-field px-2 py-2.5 transition-colors duration-200 hover:bg-base-200/60">
                    <span className="dr-tint-warning grid size-8 shrink-0 place-items-center rounded-full">
                      <Icon className="size-4" aria-hidden="true" />
                    </span>
                    <div className="min-w-0">
                      <Link to={a.href} className="font-semibold break-anywhere hover:underline">
                        {a.title}
                      </Link>
                      {a.detail ? <p className="text-sm break-anywhere text-base-content/60">{a.detail}</p> : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Section>
        <Section
          title="Recent jobs"
          className="xl:col-span-2"
          actions={
            <Link to="/jobs" className="btn btn-ghost btn-sm">
              All jobs <ArrowRight className="size-3.5" aria-hidden="true" />
            </Link>
          }
        >
          <JobsTable jobs={recentJobs} emptyTitle="No jobs yet" emptyText="Deployments, lifecycle actions and VPS enrollments appear here once someone starts one." />
        </Section>
      </div>
    </>
  );
}
